import { notifications } from '@mantine/notifications';
import type { CoordinatorRole } from '@reditelji/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, isTransient } from './client';

/** A note between the organisation and the marshal team (SPEC §1.1L). */
export interface NoteRow {
  id: string;
  volunteerId: string;
  authorId: string;
  authorName: string;
  authorRole: CoordinatorRole;
  body: string;
  createdAt: string;
}

export function useNotes(volunteerId: string) {
  return useQuery({
    queryKey: ['notes', volunteerId],
    queryFn: () => api<NoteRow[]>(`/volunteers/${volunteerId}/notes`),
  });
}

export function useNoteMutations(volunteerId: string) {
  const qc = useQueryClient();
  // The roster badge and Pregled's Organizacija row are read from the same notes.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['notes', volunteerId] });
    void qc.invalidateQueries({ queryKey: ['volunteers'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };
  const add = useMutation({
    mutationFn: (body: string) =>
      api<NoteRow>(`/volunteers/${volunteerId}/notes`, { method: 'POST', body: JSON.stringify({ body }) }),
    onError: (err: Error) => notifications.show({ color: 'red', title: 'Ni poslano', message: err.message }),
    onSettled: invalidate,
  });
  const markRead = useMutation({
    mutationFn: (upToNoteId: string) =>
      api<{ ok: true }>(`/volunteers/${volunteerId}/notes/read`, {
        method: 'POST',
        body: JSON.stringify({ upToNoteId }),
      }),
    // Nothing on screen asks again if it fails, so it asks again itself —
    // for a dropped connection or a busy server, not for a refusal, which
    // three more tries would only repeat.
    retry: (count, err) => count < 3 && isTransient(err),
    onSettled: invalidate,
  });
  return { add, markRead };
}
